import { io, Socket } from 'socket.io-client';

// Remove /api suffix from API URL for WebSocket connection
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const SOCKET_URL = API_URL.replace(/\/api\/?$/, '');

/**
 * WebSocket event handlers
 */
interface WebSocketHandlers {
  onMessageChunk?: (data: { conversationId: string; content: string; done: boolean }) => void;
  onMessageComplete?: (data: {
    conversationId: string;
    messageId: string;
    content: string;
    tokenCount: number;
  }) => void;
  onError?: (data: { code: string; message: string }) => void;
  onTypingIndicator?: (data: { conversationId: string; isTyping: boolean }) => void;
  onTitleUpdated?: (data: { conversationId: string; title: string }) => void;
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
}

/**
 * WebSocket client singleton
 */
class WebSocketService {
  private socket: Socket | null = null;
  private handlers: WebSocketHandlers = {};
  private token: string | null = null;

  /**
   * Connect to WebSocket server
   */
  connect(token: string): void {
    if (this.socket?.connected) {
      return;
    }

    this.token = token;

    this.socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    this.setupEventListeners();
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Set up event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('WebSocket connected');
      this.handlers.onConnect?.();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      this.handlers.onDisconnect?.(reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message);
      this.handlers.onError?.({
        code: 'CONNECTION_ERROR',
        message: error.message,
      });
    });

    this.socket.on('message_chunk', (data) => {
      this.handlers.onMessageChunk?.(data);
    });

    this.socket.on('message_complete', (data) => {
      this.handlers.onMessageComplete?.(data);
    });

    this.socket.on('error', (data) => {
      console.error('WebSocket error:', data);
      this.handlers.onError?.(data);
    });

    this.socket.on('typing_indicator', (data) => {
      this.handlers.onTypingIndicator?.(data);
    });

    this.socket.on('title_updated', (data) => {
      this.handlers.onTitleUpdated?.(data);
    });
  }

  /**
   * Set event handlers
   */
  setHandlers(handlers: WebSocketHandlers): void {
    this.handlers = { ...this.handlers, ...handlers };
  }

  /**
   * Join a conversation room
   */
  joinConversation(conversationId: string): void {
    console.log('joinConversation called:', conversationId, 'connected:', this.socket?.connected);
    if (!this.socket?.connected) {
      console.warn('WebSocket not connected, cannot join conversation');
      return;
    }
    console.log('Emitting join_conversation event');
    this.socket.emit('join_conversation', conversationId);
  }

  /**
   * Leave a conversation room
   */
  leaveConversation(conversationId: string): void {
    if (!this.socket?.connected) {
      return;
    }
    this.socket.emit('leave_conversation', conversationId);
  }

  /**
   * Send a message
   */
  sendMessage(conversationId: string, content: string): void {
    console.log('sendMessage called:', { conversationId, content, connected: this.socket?.connected });
    if (!this.socket?.connected) {
      console.error('WebSocket not connected');
      this.handlers.onError?.({
        code: 'NOT_CONNECTED',
        message: 'WebSocket is not connected',
      });
      return;
    }

    console.log('Emitting send_message event');
    this.socket.emit('send_message', { conversationId, content });
  }

  /**
   * Send typing start indicator
   */
  startTyping(conversationId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit('typing_start', conversationId);
  }

  /**
   * Send typing stop indicator
   */
  stopTyping(conversationId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit('typing_stop', conversationId);
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Reconnect with current token
   */
  reconnect(): void {
    if (this.token) {
      this.disconnect();
      this.connect(this.token);
    }
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
